import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation';
import { useCallback, useMemo } from 'react';

/**
 * Represents the props for the `useNavigation` hook.
 */
interface IProps {
  /**
   * If true, the `searchParams` will be overwritten when calling `router.push(...)`.
   * @default false
   */
  overwriteSearchParams?: boolean;
  /**
   * If true, the page will scroll to the top when `router.push(...)` is called.
   * @default false
   */
  scrollOnPush?: boolean;
  /**
   * If a param or searchParam is not present in the URL, the default value will be used.
   */
  defaults?: {
    /**
     * The default values for the `params`.
     */
    params?: Record<string, string>;
    /**
     * The default values for the `searchParams`.
     */
    searchParams?: Record<string, string>;
  };
}

interface INavigation {
  currentPath: string;
  params: Record<string, string | undefined>;
  searchParams: Record<string, string | undefined>;
  push: (
    path?: string,
    queryParams?: Record<string, any>,
    overwriteSearchParams?: boolean,
  ) => void;
}

type UseNavigation = (props?: IProps) => INavigation;

export const useNavigation: UseNavigation = (props) => {
  const router = useRouter();
  const pathname = usePathname();
  const nextParams = useParams();
  const nextSearchParams = useSearchParams();
  const currentPath = `${pathname}?${nextSearchParams.toString()}`;

  const push = useCallback<INavigation['push']>(
    (path = pathname, queryParams = {}, overwriteSearchParams = false) => {
      const params = new URLSearchParams(
        overwriteSearchParams || props?.overwriteSearchParams === true
          ? undefined
          : nextSearchParams,
      );

      for (const [key, value] of Object.entries(queryParams)) {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      }

      router.push(
        path +
          (path === '' || path === pathname ? `?${params.toString()}` : ''),
        { scroll: props?.scrollOnPush ?? false },
      );
    },
    [
      router,
      pathname,
      nextSearchParams,
      props?.scrollOnPush,
      props?.overwriteSearchParams,
    ],
  );

  const params = useMemo<INavigation['params']>(() => {
    const params: INavigation['params'] = props?.defaults?.params ?? {};

    for (const [key, value] of Object.entries(nextParams)) {
      params[key] = typeof value === 'string' ? value : value?.join(',');
    }

    return params;
  }, [nextParams, props?.defaults?.params]);

  const searchParams = useMemo<INavigation['searchParams']>(() => {
    const searchParams: INavigation['searchParams'] =
      props?.defaults?.searchParams ?? {};

    for (const [key, value] of nextSearchParams.entries()) {
      if (searchParams[key]) {
        searchParams[key] = `${searchParams[key]},${value}`;
      } else {
        searchParams[key] = value;
      }
    }

    return searchParams;
  }, [nextSearchParams, props?.defaults?.searchParams]);

  return {
    push,
    params,
    currentPath,
    searchParams,
  };
};
